import { ChangeEvent, FC, useState } from "react";
import { useTranslation } from "react-i18next";
import useStyles from "./styles";
import { CustomTypography } from "../../../../components/CustomTypography/CustomTypography";
import { CustomTextInput } from "../../../../components/CustomTextInput/CustomTextInput";
import { CustomButton } from "../../../../components/CustomButton/CustomButton";
import { IconWithBackground } from "../../../../components/IconWithBackground/IconWithBackground";
import { colors } from "../../../../styles/colors";
import { CopyToClipboard } from "react-copy-to-clipboard";
import toast from "react-hot-toast";
import { CustomUploadButton } from "../../../../components/CustomUploadButton/CustomUploadButton";
import { Icon } from "../../../../components/Icon/Icon";
import { CustomSearch } from "../../../../components/CustomSearch/CustomSearch";
import { validateEmail } from "../../../../utils/regex";
import {
  UserRole,
  useGetAllEmployeeWithoutVehicle,
  useInviteEmployeeCompanie,
} from "../../../../api/employee";
import { getCurrentFleetId } from "../../../../utils/getCurrentFleet";

type Props = {
  onClickSend?: () => void;
  onChangeCurrentEmployee: (employee: TEmployeeWithoutVehicle | null) => void;
};

export const StepSearchCollaborator: FC<Props> = ({
  onClickSend,
  onChangeCurrentEmployee,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { mutate: inviteUser, isPending: loadingUserInvite } =
    useInviteEmployeeCompanie(getCurrentFleetId());
  const { employees } = useGetAllEmployeeWithoutVehicle(getCurrentFleetId());

  const [email, setEmail] = useState("");
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const handleCopy = () => {
    toast.success(
      t("collaborator.copySuccess", "Copy completed successfully !")
    );
  };

  const onDrop = (acceptedFiles: File[]) => {
    setUploadedFile(acceptedFiles[0]);
  };

  const onSendInvitationEmail = () => {
    if (!validateEmail(email)) {
      toast.error(t("required.emailInvalid", "Invalid email"));
    } else {
      if (firstname.trim().length === 0 || lastname.trim().length === 0) {
        toast.error(t("required.checkFields", "All fields are required"));
      } else {
        inviteUser({ email, role: UserRole.EMPLOYEE, firstname, lastname });
      }
    }
  };

  // Build the same label the Autocomplete displays, so we can match what the
  // user typed against the available employees.
  const getEmployeeLabel = (user: any) =>
    `${user?.firstname ?? ""} ${user?.lastname ?? ""}`.trim();

  // Only a real employee object (one that carries a user_id) is a valid
  // selection. Free-text typed into the freeSolo Autocomplete is a string and
  // must never be treated as a collaborator.
  const isValidEmployee = (value: any): value is TEmployeeWithoutVehicle =>
    !!value && typeof value === "object" && value.user_id != null;

  const onChangeTextField = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.target.value.trim().length === 0) {
      onChangeCurrentEmployee(null);
    }
  };

  const onChangeAutocomplet = (_: any, valueTrue: any) => {
    // Reject free-text strings; keep the previous selection untouched unless a
    // real employee is chosen.
    onChangeCurrentEmployee(isValidEmployee(valueTrue) ? valueTrue : null);
  };

  // Resolve what the user types to a real employee. This makes "Envoyer" work
  // even when the user types a full name and hits enter instead of clicking an
  // option in the dropdown (previous behaviour left currentEmployee null and
  // always raised "Vérifier le nom du collaborateur").
  const onInputChangeSearch = (_: any, inputValue: string, reason: string) => {
    // `reset` fires programmatically after a selection; ignore it so we don't
    // clobber the freshly selected employee.
    if (reason === "reset") return;

    const trimmed = (inputValue ?? "").trim();
    if (trimmed.length === 0) {
      onChangeCurrentEmployee(null);
      return;
    }

    const normalized = trimmed.toLowerCase();
    const matches = employees.filter(
      (user: any) => getEmployeeLabel(user).toLowerCase() === normalized
    );

    // Unique exact match -> treat as a valid selection.
    onChangeCurrentEmployee(matches.length === 1 ? matches[0] : null);
  };

  return (
    <div className={classes.container}>
      <div className={classes.divLeft}>
        <div className={classes.subDivInvit}>
          <CustomTypography
            text={t("collaborator.invite", "Invite a collaborator")}
            variant={"h3"}
            isUppercase
          />
          <div className={classes.divSendEmail}>
            <CustomTextInput
              label={`${t("collaborator.name", "Nom")} *`}
              placeholder={t("collaborator.name", "Nom")}
              onChange={(e) => setLastname(e.target.value)}
            />
            <CustomTextInput
              label={`${t("collaborator.firstName", "Firstname")} *`}
              placeholder={t("collaborator.firstName", "Firstname")}
              onChange={(e) => setFirstname(e.target.value)}
            />
            <CustomTextInput
              label={`${t(
                "collaborator.emailPerso",
                "Personal email of your employee"
              )} *`}
              placeholder={t(
                "collaborator.placeholderEmail",
                "collaborator@mail.com"
              )}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <div className={classes.divSendEmail2}>
              <CustomButton
                label={t("common.send", "Send")}
                variant={"primary"}
                width={"fit-content"}
                height={"40px"}
                loading={loadingUserInvite}
                onClick={onSendInvitationEmail}
              />
              <CopyToClipboard text={email} onCopy={handleCopy}>
                <IconWithBackground
                  iconName="copy"
                  backgroundColor={colors.primary}
                  height={"20px"}
                  width={"20px"}
                  iconColor={colors.WHITE}
                />
              </CopyToClipboard>
            </div>
          </div>
        </div>

        <div className={classes.subDivInvit}>
          <CustomTypography
            text={t("collaborator.invitList", "Invite a list of collaborators")}
            variant={"h3"}
            isUppercase
          />
          <div className={classes.divSendEmail}>
            <div className={classes.divText}>
              <CustomTypography
                text={t("collaborator.loadFile", "Load a .csv file")}
                variant="link"
              />
              <div className={classes.divTextDisplay}>
                <CustomTypography
                  text={uploadedFile?.name ?? ""}
                  variant={"default"}
                />
                <Icon name={"paperClip"} width={"20px"} height={"20px"} />
              </div>
            </div>
            <div className={classes.divSendEmail2}>
              <CustomUploadButton onDrop={onDrop} />
              <CustomButton
                label={t("common.send", "Send")}
                variant={"disabled"}
                width={"fit-content"}
                height={"40px"}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={classes.subDivInvit}>
        <CustomTypography
          text={t("collaborator.collaboratorSearch", "Collaborator search")}
          variant={"h3"}
          isUppercase
        />
        <div className={classes.divSendEmail}>
          <CustomSearch
            label={t(
              "collaborator.nameOfCollab",
              "Name of your collaborator *"
            )}
            placeholder={t("collaborator.placeholderName", "NAME_COLLABORATOR")}
            options={employees}
            onChange={onChangeAutocomplet}
            onInputChange={onInputChangeSearch}
            onChangeTextField={onChangeTextField}
            isOptionEqualToValue={(option: any, value: any) =>
              option?.user_id === value?.user_id
            }
            getOptionLabel={(user: any) =>
              typeof user === "string" ? user : getEmployeeLabel(user)
            }
          />
          <div className={classes.divSendEmail2}>
            <CustomButton
              onClick={onClickSend}
              label={t("common.send", "Send")}
              variant={"primary"}
              width={"fit-content"}
              height={"40px"}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
